import type { TStoreName } from "../types/store";
import { buildDocument } from "./build-document";
import { APP_NAME, STORE_SUFFIX } from "./store";

/** 스토어별로 다른 쿼리만 키로 두고, 나머지는 default 에 `${S}` 보간으로 한 벌만 유지한다. */
const withFragments = (operations: Partial<Record<TStoreName, string>> & { default: string }) =>
  buildDocument(operations[APP_NAME] ?? operations.default);

const S = STORE_SUFFIX;

export const collectionsQuery = buildDocument(`#graphql
  query CollectionsTitle {
    collections(first: 250, sortKey: TITLE) {
      edges {
        node {
          id
          title
          handle
          brands: metafield(namespace: "type", key: "brands") {
            ...MetafieldFields
          }
          type: metafield(namespace: "collection", key: "type") {
            ...MetafieldFields
          }
          tag: metafield(namespace: "operation", key: "tag") {
            ...MetafieldFields
          }
        }
      }
    }
  }
`);

export const collectionsFiltersQuery = buildDocument(`#graphql
  query CollectionFilters($first: Int!) {
    collections(first: 250) {
      edges {
        node {
          id
          title
          handle
          products(first: $first) {
            filters {
              ...FilterFields
            }
          }
        }
      }
    }
  }
`);

/** 컬렉션 상세 (handle) — 무한스크롤 목록 */
export const collectionHandleQuery = withFragments({
  default: `#graphql
    query CollectionHandle${S}($handle: String!, $first: Int!, $after: String, $filters: [ProductFilter!], $sortKey: ProductCollectionSortKeys!, $reverse: Boolean) {
      collection(handle: $handle) {
        ...CollectionDetailBaseFields
        products(first: $first, after: $after, filters: $filters, sortKey: $sortKey, reverse: $reverse) {
          filters { ...FilterFields }
          edges { node { ...CommonProductFields${S} } }
          pageInfo { ...PageInfoFields }
        }
        accessible_memberships: metafield(namespace: "custom", key: "accessible_memberships") { ...MetafieldFields }
      }
    }
  `,
});

export const collectionQuickAddQuery = withFragments({
  default: `#graphql
    query CollectionQuickAdd${S}($handle: String!, $first: Int!, $after: String, $filters: [ProductFilter!], $sortKey: ProductCollectionSortKeys!, $reverse: Boolean) {
      collection(handle: $handle) {
        id
        title
        handle
        products(first: $first, after: $after, filters: $filters, sortKey: $sortKey, reverse: $reverse) {
          filters { ...FilterFields }
          edges { node { ...CommonCollectionProductFields${S} } }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
});

export const collectionsTitleQuery = withFragments({
  default: `#graphql
    query CollectionsByTitle${S}($first: Int!, $query: String) {
      collections(first: $first, query: $query) {
        edges {
          node {
            id
            title
            handle
            image { ...ImageFields }
            products(first: 250) {
              edges {
                node {
                  ...ProductCommonFields
                }
              }
            }
          }
        }
      }
    }
  `,
  tune: `#graphql
    query CollectionsByTitleTune($first: Int!, $query: String) {
      collections(first: $first, query: $query) {
        edges {
          node {
            id
            title
            handle
            image { ...ImageFields }
            products(first: 250) {
              edges {
                node {
                  ...ProductFieldsTune
                dropDate: metafield(namespace: "operation", key: "drop_date") { ...MetafieldFields }
                }
              }
            }
          }
        }
      }
    }
  `,
  wellbeingexpress: `#graphql
    query CollectionsByTitleWellbeingexpress($first: Int!, $query: String) {
      collections(first: $first, query: $query) {
        edges {
          node {
            id
            title
            handle
            image { ...ImageFields }
            products(first: 250) {
              edges {
                node {
                  ...ProductFieldsWellbeingexpress
                }
              }
            }
          }
        }
      }
    }
  `,
});

/** 단일 컬렉션 상세 (무한스크롤) */
export const collectionQuery = withFragments({
  default: `#graphql
    query Collection${S}($handle: String, $first: Int, $after: String, $filters: [ProductFilter!], $sortKey: ProductCollectionSortKeys, $reverse: Boolean) {
      collection(handle: $handle) {
        id
        handle
        products(first: $first, after: $after, filters: $filters, sortKey: $sortKey, reverse: $reverse) {
          edges { node { ...CollectionDetailProductFields${S} } }
          pageInfo { ...PageInfoFields }
        }
      }
    }
  `,
});
